import React from 'react';
import * as R from 'ramda';
import moment from 'moment';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import * as sowTypes from 'sow/types';

const getProp = (propName, defaultValue = null) => props => {
  return R.propOr(defaultValue, propName)(props);
};

const formatDate = datetime => moment.utc(datetime).format('YYYY-MM-DD');
const formatTime = datetime => moment.utc(datetime).format('kk:mm:ss');

export const FormattedDatetime = ({ datetime, hideTime }) => {
  const diplayDate = formatDate(datetime);
  const diplayTime = hideTime ? '' : formatTime(datetime);
  return (
    <span>
      {diplayDate} {diplayTime} (UTC)
    </span>
  );
};

FormattedDatetime.propTypes = {
  datetime: sowTypes.datetimeType,
  hideTime: PropTypes.bool,
};

export const Box = styled.div`
  page-break-before: ${props => {
    if (props.pageBreak === 'always') return 'always';
    if (props.pageBreak === true) return 'avoid';
    return null;
  }};
`;

export const SectionHeading = styled(Box).attrs({ as: 'h2' })`
  clear: both;
  display: block;
`;

export const SubSectionHeading = styled(Box).attrs({
  as: 'h3',
  pageBreak: true,
})`
  text-align: left;
  color: #000000;
  margin-bottom: 10px;
`;

export const WorksheetHeader = styled(Box).attrs({ as: 'p' })`
  white-space: pre-line;
  margin-top: 0;
  margin-bottom: 0;
`;

export const WorksheetTableBase = styled(Box).attrs({ as: 'table' })`
  border: 1px solid #000;
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;

  tr,
  td {
    page-break-inside: avoid;
    border: 1px solid;
  }

  td.empty {
    background: #eee;
    font-weight: bold;
    padding: 10px;
    text-align: center;
  }

  tr.note-row,
  tr.note-row td.note-column {
    background-color: lightyellow;
    font-family: Courier;
    font-weight: lighter;
    padding: 0;
    margin: 0;
  }
  .note-label,
  .note-marker {
    float: left;
    padding: 3px 5px;
    margin-right: 5px;
    background-color: #f58d3d;
    color: #f5f5f5;
    font-weight: 500;
    font-size: 0.8em;
    min-height: 1.25em;
    display: inline-block;
  }
  .note-marker {
    margin-top: 5px;
  }
  .note-details {
    padding: 3px 5px 0 3px;
    color: #31708f;
    background-color: #d9edf7;
  }
  .note-datetime,
  .note-author {
    color: #777;
  }
  .note-datetime {
    float: right;
  }
  .note-text {
    clear: both;
    overflow: hidden;
    width: 100%;
    padding: 3px;
    color: #3c2611;
    background-color: #fcf8e3;
  }
  .note-icon {
    width: 0.8rem;
    height: 0.8rem;
    display: inline-block;
    vertical-align: text-bottom;
    padding-right: 5px;
    background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAMAAAD04JH5AAAAulBMVEUAAAD////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////yHCTBAAAAPXRSTlMAAQIEBQkMDg8REhMUFxwfJigtMTI6Oz0+REdRUlZXWVxiaGlzdHd5hZqqsLK3wMHT2drc4Obo7e/x9fv90W4zZgAAAVVJREFUeNrt2MlWwkAUhOHqEAEFFecRUHEAR4wDBOz3fy0XnBMZYuLCvrWpWuf0/21vAACN68Rbb/rSiTGbu/OcTQ5n/XtPWwcAbjxxLaDO7PshcEkF+C08cwFtjLiAW3D7vi+AAAIIIIAAAggggAACCCCAAAIIIIAAAggggAACCCCAAAIIIIAAAggggAACCCCAAOEAw9P1OCqdCwY4c/jbAvV3ASqgDS4gcWTAOciATTZgjQ2IBcied83iVUIDqiVfNsiACciAARuwTwaMHRlwBS4gjcmAE3ABT+ACkpgLSOugAtImqICsTwL89DmAuT4FMN9nABb6BMBi3x6w1DcHLPetASt9Y8Bq3xaQ08eXISCvjzc7QG4fAzPAOLePAyvAay3/NnTvNoDHXw/WbQvA6KjgPt6bhgZ8dCuFF/rGQ0jAZ3+n/JdV7fii95+Lspejakn6Gy2WClxfgZ8EAAAAAElFTkSuQmCC);
    background-position: left;
    background-size: contain;
    background-repeat: no-repeat;
  }

  tr.comment-row,
  tr.comment-row td.comment-column {
    background-color: lightyellow;
    font-family: Courier;
    font-weight: lighter;
    padding: 0;
    margin: 0;
  }
  .comment-label,
  .comment-marker {
    float: left;
    padding: 3px 5px;
    margin-right: 5px;
    background-color: #9574ca;
    color: #eee;
    font-size: 0.8em;
    min-height: 1.25em;
    font-weight: 500;
    display: inline-block;
  }
  .comment-marker {
    margin-top: 5px;
  }
  .comment-details {
    padding: 3px 5px 0 3px;
    background-color: #d9edf7;
  }
  .comment-datetime,
  .comment-author {
    color: #777;
  }
  .comment-datetime {
    float: right;
  }
  .comment-text {
    clear: both;
    overflow: hidden;
    width: 100%;
    padding: 3px;
    color: #3c2611;
    background-color: #f8f5fe;
  }
  .comment-icon {
    width: 0.8rem;
    height: 0.8rem;
    display: inline-block;
    vertical-align: text-bottom;
    padding-right: 5px;
    background-image: url(data:image/png;base64,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);
    background-position: left;
    background-size: contain;
    background-repeat: no-repeat;
  }
`;

export const WorksheetStandardTable = styled(WorksheetTableBase)`
  & td {
    width: 55%;
    padding-left: 5px;
    background-color: #f3f3f3;
    border: 1px solid #000;
    vertical-align: top;
    padding: 8px;
  }

  .non-conditional {
    background-color: #b7dde8;
    font-weight: bold;
  }

  .conditional-question {
    background-color: #def4fb;
    font-weight: bold;
  }

  .conditional-question p {
    margin-left: 30px;
  }
`;

export const WorksheetMatrixTable = styled(WorksheetTableBase)`
  text-align: left;
  word-break: break-word;

  & th {
    background-color: #b7dde8;
    border: 1px black solid;
  }

  & td,
  th {
    padding: 8px;
    vertical-align: top;
  }
`;

export const WorksheetLocationContainer = styled(Box)`
  .location-table table {
    table-layout: fixed;
    border: none;
  }

  .location-table tr {
    border: none;
  }

  .location-table .location-header th {
    background-color: #3279b7;
    height: 35px;
    color: white;
    font-size: 18px;
    text-align: left;
    padding: 5px;
  }

  .location-table .location-body td {
    background-color: #f3f3f3;
    border: none;
    padding: 5px;
  }

  .location-table .location-body .headers {
    font-weight: bold;
  }
`;

// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// -
// TODO remove this once we convert it to styled-components
const exportCss = `
h2 {
  clear: both;
  display: block;
  /* page-break-before: always; */
}

h3 {
  text-align: center;
  color: #666;
  margin-bottom: 0;
}

p {
  white-space: pre-line;
  margin-top: 0;
  margin-bottom: 0;
}

table {
  border: 1px solid #000;
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;
}

table.matrix {
  table-layout: fixed;
}

tr, td {
  page-break-inside: avoid;
  border: 1px solid;
}

.single td {
  width: 66%;
  padding-left: 5px;
  background-color: #f3f3f3;
  border: 1px solid #000;
}

.single td:first-child, .matrix th {
  background-color: #b7dde8;
  /* font-weight: 700; */
}

td.empty {
  background: #eee;
  font-weight: bold;
  padding: 10px;
  text-align: center;
}

tr.note-row, tr.note-row td.note-column {
  background-color: lightyellow;
  font-family: Courier;
  font-weight: lighter;
  padding: 0;
  margin: 0;
}
.note-label {
  float: left;
  padding: 3px 5px;
  margin-right: 5px;
  background-color: yellow;
}
.note-details {
  padding: 3px 5px 0 3px;
  color: #31708f;
  background-color: #d9edf7;
}
.note-datetime,
.note-author {
  color: #777;
}
.note-datetime {
  float: right;
}
.note-text {
  clear: both;
  overflow: hidden;
  width: 100%;
  padding: 3px;
  color: #3C2611;
  background-color: #FCF8E3;
}

.break {
  page-break-before: avoid;
}

.must-break {
  page-break-before: always;
}
`;
